import {
  GetCurrencyConversionResponse,
  GetWorkerBalancesResponse,
  GetWorkerTransactionsResponse,
  WorkerTransaction,
  WorkerWithdrawalTrackingResponse,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bank';
import useSWR from 'swr';

import { globalFetcherSettings } from 'lib/constants';
import { useTheraFernClient } from './useFern';

export const useGetWorkerBalance = ({ userId, companyId }: { userId: string; companyId: string }) => {
  const { theraFernApi } = useTheraFernClient();
  const getWorkerBalanceFetcher = async (): Promise<GetWorkerBalancesResponse> => {
    const response = await theraFernApi.thera.bank.bankApi.getWorkerBalance(userId, companyId);
    return response;
  };
  const { data, error, mutate, isLoading } = useSWR(
    userId && companyId ? ['getWorkerBalanceFetcher', userId, companyId] : false,
    getWorkerBalanceFetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    balances: data?.balances || [],
    mutate,
    isPending: isLoading,
    error,
  };
};

export const useGetWorkerTransactions = ({
  userId,
  companyId,
  type,
}: {
  userId: string;
  companyId: string;
  type?: string;
}) => {
  const { theraFernApi } = useTheraFernClient();
  const getWorkerTransactionsFetcher = async (): Promise<GetWorkerTransactionsResponse> => {
    const response = await theraFernApi.thera.bank.bankApi.getWorkerTransactions(userId, companyId, { type });
    return response;
  };
  const { data, error, mutate, isLoading } = useSWR(
    userId && companyId ? ['getWorkerTransactionsFetcher', userId, companyId, type] : false,
    getWorkerTransactionsFetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  // Manual fetcher for cursor-based pagination
  const fetchWithCursor = async (afterCursor: string): Promise<GetWorkerTransactionsResponse> => {
    const response = await theraFernApi.thera.bank.bankApi.getWorkerTransactions(userId, companyId, {
      afterCursor,
      type,
    });
    return response;
  };

  return {
    transactions: data?.transactions || [],
    nextCursor: data?.nextCursor,
    mutate,
    isPending: isLoading,
    error,
    fetchWithCursor,
  };
};

export const useGetWorkerTransactionById = ({
  userId,
  companyId,
  transactionId,
}: {
  userId: string;
  companyId: string;
  transactionId: string;
}) => {
  const { theraFernApi } = useTheraFernClient();
  const getWorkerTransactionByIdFetcher = async (): Promise<WorkerTransaction> => {
    const response = await theraFernApi.thera.bank.bankApi.getWorkerTransactionById(userId, companyId, transactionId);
    return response;
  };
  const { data, error, mutate, isLoading } = useSWR(
    userId && companyId ? ['getWorkerTransactionByIdFetcher', userId, companyId, transactionId] : false,
    getWorkerTransactionByIdFetcher,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    transaction: data,
    error,
    isPending: isLoading,
    mutate,
  };
};

/**
 * Package-style tracking timeline for a single withdrawal.
 *
 * Lazy by design: pass `enabled: false` (e.g. until the tracking panel is opened) and no request
 * fires. This keeps the provider live-status calls (Wise/dLocal) off the withdrawals list and avoids
 * rate-limiting — the fetch only happens when the caller actually looks at the tracking view.
 */
export const useWorkerWithdrawalTracking = ({
  withdrawalId,
  enabled = true,
}: {
  withdrawalId?: string;
  enabled?: boolean;
}) => {
  const { theraFernApi } = useTheraFernClient();
  const getWorkerWithdrawalTrackingFetcher = async (): Promise<WorkerWithdrawalTrackingResponse> => {
    const response = await theraFernApi.thera.bank.bankApi.getWorkerWithdrawalTracking(withdrawalId);
    return response;
  };
  const { data, error, mutate, isLoading } = useSWR(
    enabled && withdrawalId ? ['getWorkerWithdrawalTracking', withdrawalId] : false,
    getWorkerWithdrawalTrackingFetcher,
    {
      ...globalFetcherSettings,
      // Returning without calling revalidate() disables SWR retries entirely. Only give up on 404s
      // (withdrawal not trackable) or after a few attempts; otherwise retry transient 5xx/network errors
      // so a temporary blip doesn't leave the panel stuck on "not available".
      onErrorRetry: (error, _key, _config, revalidate, { retryCount }) => {
        if (error?.status === 404 || retryCount >= 3) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    },
  );

  return {
    tracking: data,
    error,
    isPending: isLoading,
    mutate,
  };
};

/** @deprecated */
export const useGetCurrencyConversion = ({ from, to, amount }: { from: string; to: string; amount: number }) => {
  const { theraFernApi } = useTheraFernClient();
  const getCurrencyConversion = async (): Promise<GetCurrencyConversionResponse> => {
    const response = await theraFernApi.thera.bank.bankApi.getCurrencyConversion({
      from,
      to,
      amount,
    });
    return response;
  };
  const { data, error, mutate, isLoading } = useSWR(
    from && to && amount ? ['getCurrencyConversion', from, to, amount] : false,
    getCurrencyConversion,
    {
      ...globalFetcherSettings,
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
    },
  );

  return {
    data,
    mutate,
    isPending: isLoading,
    error,
  };
};
